import React from 'react';
import { X, Code, Video, Share2 } from 'lucide-react';
import type { HtmlEmbedEditModalProps, EmbedTab } from './types';
import { useHtmlEmbedForm } from './useHtmlEmbedForm';
import { CustomHtmlTab } from './CustomHtmlTab';
import { VideoTab } from './VideoTab';
import { IframeTab } from './IframeTab';
import { DisplaySettings } from './DisplaySettings';

export const HtmlEmbedEditModal: React.FC<HtmlEmbedEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  isLoading = false,
}) => {
  const {
    formData,
    activeTab,
    setActiveTab,
    handleInputChange,
    handleTopLevelChange,
    handleVideoUrlPaste,
  } = useHtmlEmbedForm(isOpen, initialData);

  const handleSave = () => {
    const saveData: any = {
      embedType: activeTab === 'custom' ? 'custom' : activeTab === 'video' ? 'video' : 'iframe',
      responsive: formData.responsive,
      lazyLoad: formData.lazyLoad,
    };

    // Add the relevant embed data based on active tab
    if (activeTab === 'custom') {
      saveData.custom = formData.custom;
    } else if (activeTab === 'video') {
      saveData.video = formData.video;
    } else if (activeTab === 'iframe') {
      saveData.iframe = formData.iframe;
    }

    // Add container style if any values are set
    const hasContainerStyle = Object.values(formData.containerStyle).some(v => v !== '');
    if (hasContainerStyle) {
      saveData.containerStyle = formData.containerStyle;
    }

    console.log('Saving HTML Embed data:', saveData);
    onSave(saveData);
  };

  if (!isOpen) return null;

  const tabs: { id: EmbedTab; label: string; icon: React.ComponentType<{ size: number }> }[] = [
    { id: 'custom', label: 'Custom HTML', icon: Code },
    { id: 'video', label: 'Video', icon: Video },
    { id: 'iframe', label: 'Generic Iframe', icon: Share2 },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Code className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Embed Content</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <tab.icon size={16} />
                  <span>{tab.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'custom' && (
            <CustomHtmlTab
              data={formData.custom}
              onChange={(field, value) => handleInputChange('custom', field, value)}
            />
          )}
          {activeTab === 'video' && (
            <VideoTab
              data={formData.video}
              onChange={(field, value) => handleInputChange('video', field, value)}
              onVideoUrlPaste={handleVideoUrlPaste}
            />
          )}
          {activeTab === 'iframe' && (
            <IframeTab
              data={formData.iframe}
              onChange={(field, value) => handleInputChange('iframe', field, value)}
            />
          )}

          {/* Common Settings */}
          <DisplaySettings
            responsive={formData.responsive}
            lazyLoad={formData.lazyLoad}
            onChange={handleTopLevelChange}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Embed'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HtmlEmbedEditModal;
